import { useEffect } from 'react';
import { useGlobalStore } from '@/scripts/store/globalStore';
import { UrlStateManager } from '@/utils/UrlStateManager';
import { urlStrategies } from '@/scripts/types/urlSync';
import { createUrlSync } from './createUrlSync';
import { clampNumberToBounds, clampRangeToBounds } from '@/scripts/utils/clamp';
import { FacetDTO, FacetSectionType } from '@/scripts/domain/Facets';
import { SearchType } from '@/scripts/domain/shared';

const urlStateManager = new UrlStateManager();

// Robustly read a query param across possible CMS iframe contexts
const getSearchParamAnyContext = (key: string): string | null => {
  // 1) Current window
  try {
    const current = new URLSearchParams(window.location.search).get(key);
    if (current) return current;
  } catch {
    // Ignore parsing errors
  }

  // 2) Top window (same-origin only)
  try {
    if (window.top && window.top !== window) {
      const topValue = new URLSearchParams(window.top.location.search).get(key);
      if (topValue) return topValue;
    }
  } catch {
    // Likely cross-origin access to top window; ignore
  }

  // 3) Parent window (same-origin only)
  try {
    if (window.parent && window.parent !== window) {
      const parentValue = new URLSearchParams(window.parent.location.search).get(key);
      if (parentValue) return parentValue;
    }
  } catch {
    // Likely cross-origin access to parent window; ignore
  }

  // 4) Referrer (works even if cross-origin)
  try {
    if (document.referrer) {
      const refUrl = new URL(document.referrer);
      const refValue = refUrl.searchParams.get(key);
      if (refValue) return refValue;
    }
  } catch {
    // Ignore malformed referrer
  }

  return null;
};

// Basic sanitization for facet IDs (allow alphanumeric, underscore, hyphen)
const sanitizeFacetId = (value: string | null | undefined): string => {
  if (!value) return '';
  const trimmed = value.trim();
  return /^[A-Za-z0-9_-]+$/.test(trimmed) ? trimmed : '';
};

/**
 * Helper function to detect if we're on a product listing page
 */
const isProductListingPage = (): boolean => {
  // Check for product listing module
  const productListingElement = document.querySelector('[data-module="productListing"]');
  if (productListingElement) {
    return true;
  }

  // Check for generic listing with product type
  const listingElement = document.querySelector('[data-module="listing"]');
  if (listingElement) {
    const dataType = (listingElement as HTMLElement).dataset.type;
    return dataType === 'product';
  }

  return false;
};

/**
 * Helper function to detect if we're on any listing page (blog, news, etc.)
 */
const isListingPage = (): boolean => {
  const isAny = !!document.querySelector('[data-module="listing"]') || isProductListingPage();
  return isAny;
};

/**
 * Helper function to detect if we're on a search listing page specifically
 */
const isSearchListingPage = (): boolean => {
  // Consider either the listing component or its filter section as an indicator
  const isAny =
    !!document.querySelector('[data-module="searchListing"]') ||
    !!document.querySelector('[data-module="sectionSearchListingFacet"]');
  return isAny;
};

/**
 * Helper function to detect the current page/listing type
 */
const getCurrentPageType = (): SearchType => {
  // Check for product listing first
  if (isProductListingPage()) {
    return 'product';
  }

  // Check for generic listing with specific type
  const listingElement = document.querySelector('[data-module="listing"]');
  if (listingElement) {
    const dataType = (listingElement as HTMLElement).dataset.type as SearchType;
    if (dataType && ['blog-post', 'news', 'customer-stories'].includes(dataType)) {
      return dataType;
    }
  }

  // Default to blog-post if we can't determine the type
  return 'blog-post';
};

/**
 * Hook for synchronizing global store state with URL parameters
 */
export const useUrlSync = () => {
  // Use hooks to subscribe to state changes
  const mockInputValue = useGlobalStore(state => state.mockInputValue);
  const setMockInputValue = useGlobalStore(state => state.setMockInputValue);
  const newsFacets = useGlobalStore(state => state.newsFacets);
  // no-op subscription removed; we use csv selectors below to trigger updates
  const setFacetsByType = useGlobalStore(state => state.setFacetsByType);
  const pageNumber = useGlobalStore(state => state.pageNumber);
  const setPageNumber = useGlobalStore(state => state.setPageNumber);
  const blogSearchValue = useGlobalStore(state => state.blogSearchValue);
  const newsSearchValue = useGlobalStore(state => state.newsSearchValue);
  const setSearchValueByType = useGlobalStore(state => state.setSearchValueByType);
  const productRangeSlider = useGlobalStore(state => state.productRangeSlider);
  const setProductRangeSlider = useGlobalStore(state => state.setProductRangeSlider);
  const productSingleSlider = useGlobalStore(state => state.productSingleSlider);
  const setProductSingleSlider = useGlobalStore(state => state.setProductSingleSlider);
  // Validators for URL-provided slider values
  const validateRange = (value: unknown): [number, number] | null => {
    if (!Array.isArray(value) || value.length !== 2) return null;
    const a = Number(value[0]);
    const b = Number(value[1]);
    if (!isFinite(a) || !isFinite(b)) return null;
    return [a, b];
  };

  const validateSingle = (value: unknown): number | null => {
    const n = Number(value);
    if (!isFinite(n)) return null;
    return n;
  };
  // productFacets JSON is no longer synced via URL. Selection is derived from CSV ids.
  const setProductFacets = useGlobalStore(state => state.setProductFacets);
  const productSearchValue = useGlobalStore(state => state.productSearchValue);
  const setProductSearchValue = useGlobalStore(state => state.setProductSearchValue);
  const predictiveSearchValue = useGlobalStore(state => state.predictiveSearchValue);
  const setPredictiveSearchValue = useGlobalStore(state => state.setPredictiveSearchValue);
  const searchParameter = useGlobalStore(state => state.searchParameter);
  // Derived CSV strings for product facet sections to ensure React subscriptions trigger updates
  const csvPrimaryFunction = useGlobalStore(state =>
    state.productFacets
      .filter(f => f.sectionId === 'primary-function')
      .map(f => f.id)
      .filter(Boolean)
      .join(',')
  );
  const csvApplicationType = useGlobalStore(state =>
    state.productFacets
      .filter(f => f.sectionId === 'application-type')
      .map(f => f.id)
      .filter(Boolean)
      .join(',')
  );
  const csvPowerType = useGlobalStore(state =>
    state.productFacets
      .filter(f => f.sectionId === 'power-type')
      .map(f => f.id)
      .filter(Boolean)
      .join(',')
  );
  const csvIndustry = useGlobalStore(state =>
    state.productFacets
      .filter(f => f.sectionId === 'industry')
      .map(f => f.id)
      .filter(Boolean)
      .join(',')
  );

  // Search listing specific state
  // Also compute CSV strings reactively for brand/related topics to ensure rerenders
  const csvAnyTypeBrand = useGlobalStore(state =>
    state
      .getFacetsByType(getCurrentPageType())
      .filter(f => f.sectionId === 'brand')
      .map(f => f.id)
      .filter(Boolean)
      .join(',')
  );
  const csvAnyTypeRelated = useGlobalStore(state =>
    state
      .getFacetsByType(getCurrentPageType())
      .filter(f => f.sectionId === 'related-topics')
      .map(f => f.id)
      .filter(Boolean)
      .join(',')
  );

  // Helper to read current search listing facets at call time (avoids stale closures)
  const getCurrentSearchListingFacets = (): FacetDTO[] =>
    useGlobalStore.getState().searchListing.facets;
  const searchListingPageNumber = useGlobalStore(state => state.searchListing.pageNumber);
  const setSearchListingSearchValue = useGlobalStore(state => state.setSearchListingSearchValue);
  const setSearchListingFacets = useGlobalStore(state => state.setSearchListingFacets);
  const setSearchListingPageNumber = useGlobalStore(state => state.setSearchListingPageNumber);

  // Clean up URL parameters that shouldn't be on the current page type
  // Delay cleanup to ensure URL parameters are read first and Optimizely is ready
  useEffect(() => {
    const cleanupTimeout = setTimeout(() => {
      const currentParams = urlStateManager.getAllParams();
      const keysToRemove: string[] = [];

      // Define which parameters should be removed based on page type
      if (!isListingPage()) {
        // Not on a listing page - remove all listing-related parameters
        keysToRemove.push(
          'blogFacets',
          'newsFacets',
          'blogSearch',
          'newsSearch',
          'productSearch',
          'brand-ids',
          'related-topic-ids'
        );
      }

      // Remove page parameter if not on a listing page OR if no listing component is present
      if (!isListingPage() || !document.querySelector('[data-module="listing"]')) {
        keysToRemove.push('page');
      }

      if (!isSearchListingPage()) {
        // Not on search listing page - remove search listing parameters
        keysToRemove.push(
          'searchListingFacets',
          'search-brand-ids',
          'search-related-topic-ids',
          'search-result-type-ids',
          'primary-function-ids',
          'application-type-ids'
        );
      }

      // Remove searchListingPage parameter if not on search listing page OR if no search listing component is present
      if (!isSearchListingPage() || !document.querySelector('[data-module="searchListing"]')) {
        keysToRemove.push('searchListingPage');
      }

      if (!isProductListingPage()) {
        // Not on product listing page - remove product slider parameters
        keysToRemove.push(
          'productRangeSlider',
          'productSingleSlider',
          'primary-function-ids',
          'application-type-ids',
          'power-type-ids',
          'industry-ids'
        );
      }

      // Remove the unwanted parameters
      keysToRemove.forEach(key => {
        if (currentParams[key]) {
          urlStateManager.removeParam(key);
        }
      });
    }, 200); // Increased delay to ensure Optimizely is ready

    return () => clearTimeout(cleanupTimeout);
  }, []); // Run once on mount

  // Helper functions for related topic ID parameter
  const getRelatedTopicIdsString = (): string => csvAnyTypeRelated;

  // Search listing: helpers for brand/related topics
  const getSearchListingBrandIdsString = (): string => {
    const ids = getCurrentSearchListingFacets()
      .filter(f => f.sectionId === 'brand')
      .map(f => f.id)
      .filter(Boolean);
    return ids.join(',');
  };

  // Search listing related topics helper
  const getSearchListingRelatedTopicIdsString = (): string => {
    const ids = getCurrentSearchListingFacets()
      .filter(f => f.sectionId === 'related-topics')
      .map(f => f.id)
      .filter(Boolean);
    return ids.join(',');
  };

  // Search listing result types helper (search-listing uses result-type instead of related-topics)
  const getSearchListingResultTypeIdsString = (): string => {
    const ids = getCurrentSearchListingFacets()
      .filter(f => f.sectionId === 'result-type')
      .map(f => f.id)
      .filter(Boolean);
    return ids.join(',');
  };

  // Helper function for brand IDs
  const getBrandIdsString = (): string => csvAnyTypeBrand;

  const setRelatedTopicId = (topicId: string) => {
    if (!topicId) return;

    const currentPageType = getCurrentPageType();
    const currentFacets = useGlobalStore.getState().getFacetsByType(currentPageType);

    const isAlreadySelected = currentFacets.some(
      facet => facet.sectionId === 'related-topics' && facet.id === topicId
    );

    if (!isAlreadySelected) {
      const newTopicFacet: FacetDTO = {
        id: topicId,
        name: topicId,
        value: topicId,
        count: 0,
        sectionId: 'related-topics',
        sectionName: 'Related Topics'
      };

      setFacetsByType(currentPageType, [...currentFacets, newTopicFacet]);
    }
  };

  const setRelatedTopicIds = (csv: string) => {
    const ids = csv
      .split(',')
      .map(id => sanitizeFacetId(id))
      .filter(Boolean);
    ids.forEach(id => setRelatedTopicId(id));
  };

  // Ensure related-topic-id and brand-ids from URL are applied to facets at startup
  useEffect(() => {
    // Try multiple contexts to support CMS iframes or rewrites
    const multiFromAnyContext = getSearchParamAnyContext('related-topic-ids');
    const singleFromAnyContext = getSearchParamAnyContext('related-topic-id');
    const brandFromAnyContext = getSearchParamAnyContext('brand-ids');

    // Fallback to UrlStateManager snapshot
    const multiFromManager = urlStateManager.getParam('related-topic-ids');
    const singleFromManager = urlStateManager.getParam('related-topic-id');
    const brandFromManager = urlStateManager.getParam('brand-ids');

    const csv = (multiFromAnyContext || multiFromManager || '').trim();
    const topicId = sanitizeFacetId(singleFromAnyContext || singleFromManager || '');
    const brandCsv = (brandFromAnyContext || brandFromManager || '').trim();

    if (csv || topicId || brandCsv) {
      if (csv) setRelatedTopicIds(csv);
      else if (topicId) setRelatedTopicId(topicId);
      if (brandCsv) setBrandIds(brandCsv);
    }
    // Run only once on mount
  }, []);

  // Ensure product facet CSV params are applied at startup (robust hydration)
  useEffect(() => {
    const pfAny = getSearchParamAnyContext('primary-function-ids');
    const atAny = getSearchParamAnyContext('application-type-ids');
    const ptAny = getSearchParamAnyContext('power-type-ids');
    const indAny = getSearchParamAnyContext('industry-ids');

    const pfMgr = urlStateManager.getParam('primary-function-ids');
    const atMgr = urlStateManager.getParam('application-type-ids');
    const ptMgr = urlStateManager.getParam('power-type-ids');
    const indMgr = urlStateManager.getParam('industry-ids');

    const pf = (pfAny || pfMgr || '').trim();
    const at = (atAny || atMgr || '').trim();
    const pt = (ptAny || ptMgr || '').trim();
    const ind = (indAny || indMgr || '').trim();

    if (pf) setProductSectionIds('primary-function', pf);
    if (at) setProductSectionIds('application-type', at);
    if (pt) setProductSectionIds('power-type', pt);
    if (ind) setProductSectionIds('industry', ind);
  }, []);

  const setBrandId = (brandId: string) => {
    if (!brandId) return;
    const currentPageType = getCurrentPageType();
    const newBrandFacet: FacetDTO = {
      id: brandId,
      name: brandId,
      value: brandId,
      count: 0,
      sectionId: 'brand',
      sectionName: 'Brand'
    };

    if (currentPageType === 'product') {
      const current = useGlobalStore.getState().productFacets;
      const exists = current.some(f => f.sectionId === 'brand' && f.id === brandId);
      if (!exists) {
        setProductFacets([...current, newBrandFacet]);
      }
    } else {
      const currentFacets = useGlobalStore.getState().getFacetsByType(currentPageType);
      const exists = currentFacets.some(f => f.sectionId === 'brand' && f.id === brandId);
      if (!exists) {
        setFacetsByType(currentPageType, [...currentFacets, newBrandFacet]);
      }
    }
  };

  const setBrandIds = (csv: string) => {
    const ids = csv
      .split(',')
      .map(id => sanitizeFacetId(id))
      .filter(Boolean);
    ids.forEach(id => setBrandId(id));
  };

  // Helpers for product facet ID CSV syncing (primary-function, application-type, power-type, industry)
  // Deprecated: replaced by memoized csv* selectors above
  // const getProductSectionIdsString = (sectionId: FacetSectionType): string => {
  //   const currentPageType = getCurrentPageType();
  //   const ids = useGlobalStore
  //     .getState()
  //     .getFacetsByType(currentPageType)
  //     .filter(f => f.sectionId === sectionId)
  //     .map(f => f.id)
  //     .filter(Boolean);
  //   if (DEBUG_URL_SYNC && currentPageType === 'product') {
  //     console.log('[useUrlSync] Serialize CSV for section', sectionId, '=>', ids.join(','));
  //   }
  //   return ids.join(',');
  // };

  const setProductFacetId = (sectionId: FacetSectionType, id: string) => {
    if (!id) return;
    const currentPageType = getCurrentPageType();
    if (currentPageType !== 'product') return;
    const currentFacets = useGlobalStore.getState().getFacetsByType(currentPageType);
    const exists = currentFacets.some(f => f.sectionId === sectionId && f.id === id);
    if (!exists) {
      const newFacet: FacetDTO = {
        id,
        name: id,
        value: id,
        count: 0,
        sectionId,
        sectionName: sectionId
      };
      setFacetsByType(currentPageType, [...currentFacets, newFacet]);
    }
  };

  const setProductSectionIds = (sectionId: FacetSectionType, csv: string) => {
    const ids = csv
      .split(',')
      .map(id => sanitizeFacetId(id))
      .filter(Boolean);

    ids.forEach(id => setProductFacetId(sectionId, id));
  };

  // Search listing setters for CSV params
  const setSearchListingBrandId = (brandId: string) => {
    if (!brandId) return;
    const current = useGlobalStore.getState().searchListing.facets;
    const exists = current.some(f => f.sectionId === 'brand' && f.id === brandId);
    if (!exists) {
      const newFacet: FacetDTO = {
        id: brandId,
        name: brandId,
        value: brandId,
        count: 0,
        sectionId: 'brand',
        sectionName: 'Brand'
      };
      setSearchListingFacets([...current, newFacet]);
    }
  };

  const setSearchListingBrandIds = (csv: string) => {
    const ids = csv
      .split(',')
      .map(id => sanitizeFacetId(id))
      .filter(Boolean);
    ids.forEach(id => setSearchListingBrandId(id));
  };

  const setSearchListingRelatedTopicId = (topicId: string) => {
    if (!topicId) return;
    const current = useGlobalStore.getState().searchListing.facets;
    const exists = current.some(f => f.sectionId === 'related-topics' && f.id === topicId);
    if (!exists) {
      const newFacet: FacetDTO = {
        id: topicId,
        name: topicId,
        value: topicId,
        count: 0,
        sectionId: 'related-topics',
        sectionName: 'Related Topics'
      };
      setSearchListingFacets([...current, newFacet]);
    }
  };

  const setSearchListingRelatedTopicIds = (csv: string) => {
    const ids = csv
      .split(',')
      .map(id => sanitizeFacetId(id))
      .filter(Boolean);
    ids.forEach(id => setSearchListingRelatedTopicId(id));
  };

  const setSearchListingResultTypeId = (resultTypeId: string) => {
    if (!resultTypeId) return;
    const current = useGlobalStore.getState().searchListing.facets;
    const exists = current.some(f => f.sectionId === 'result-type' && f.id === resultTypeId);
    if (!exists) {
      const newFacet: FacetDTO = {
        id: resultTypeId,
        name: resultTypeId,
        value: resultTypeId,
        count: 0,
        sectionId: 'result-type',
        sectionName: 'Result Type'
      };
      setSearchListingFacets([...current, newFacet]);
    }
  };

  const setSearchListingResultTypeIds = (csv: string) => {
    const ids = csv
      .split(',')
      .map(id => sanitizeFacetId(id))
      .filter(Boolean);
    ids.forEach(id => setSearchListingResultTypeId(id));
  };

  // Base URL sync configurations (always included - global parameters)
  const baseConfigs = [
    {
      paramKey: 'input',
      getValue: () => mockInputValue,
      setValue: setMockInputValue,
      strategy: urlStrategies.string
    },
    {
      paramKey: searchParameter,
      getValue: () => predictiveSearchValue,
      setValue: (value: string) => {
        setPredictiveSearchValue(value);
        // Also sync with search listing value when on search listing page
        if (isSearchListingPage()) {
          setSearchListingSearchValue(value);
        }
      },
      strategy: urlStrategies.string
    },
    {
      // New CSV-based param for multiple related topics
      paramKey: 'related-topic-ids',
      getValue: getRelatedTopicIdsString,
      setValue: setRelatedTopicIds,
      strategy: urlStrategies.string
    },
    {
      // CSV-based param for brand facets
      paramKey: 'brand-ids',
      getValue: getBrandIdsString,
      setValue: setBrandIds,
      strategy: urlStrategies.string
    }
  ];

  // Listing page specific configurations (only on listing pages)
  const listingConfigs = isListingPage()
    ? [
        {
          paramKey: 'newsFacets',
          getValue: () => newsFacets,
          setValue: (facets: FacetDTO[]) => setFacetsByType('news', facets),
          strategy: { ...urlStrategies.json, shouldUpdate: () => false }
        },
        {
          paramKey: 'blogSearch',
          getValue: () => blogSearchValue,
          setValue: (value: string) => setSearchValueByType('blog-post', value),
          strategy: urlStrategies.string
        },
        {
          paramKey: 'newsSearch',
          getValue: () => newsSearchValue,
          setValue: (value: string) => setSearchValueByType('news', value),
          strategy: urlStrategies.string
        },
        // Removed productFacets JSON sync. Facets are now represented via CSV ids per section
        {
          paramKey: 'productSearch',
          getValue: () => productSearchValue,
          setValue: setProductSearchValue,
          strategy: urlStrategies.string
        }
      ]
    : [];

  // Page parameter - only include if there's actually a listing with pagination
  const pageConfig =
    isListingPage() && document.querySelector('[data-module="listing"]')
      ? [
          {
            paramKey: 'page',
            getValue: () => pageNumber,
            setValue: setPageNumber,
            strategy: urlStrategies.number
          }
        ]
      : [];

  // Search listing specific configurations (only on search listing pages)
  const searchListingConfigs = isSearchListingPage()
    ? [
        {
          paramKey: 'searchListingFacets',
          getValue: () => getCurrentSearchListingFacets(),
          setValue: setSearchListingFacets,
          strategy: { ...urlStrategies.json, shouldUpdate: () => false }
        },
        {
          paramKey: 'search-brand-ids',
          getValue: getSearchListingBrandIdsString,
          setValue: setSearchListingBrandIds,
          strategy: urlStrategies.string
        },
        {
          // Related topics selected within search listing
          paramKey: 'search-related-topic-ids',
          getValue: getSearchListingRelatedTopicIdsString,
          setValue: setSearchListingRelatedTopicIds,
          strategy: urlStrategies.string
        },
        {
          // For search listing we want to persist the selected result types, not related topics
          paramKey: 'search-result-type-ids',
          getValue: getSearchListingResultTypeIdsString,
          setValue: setSearchListingResultTypeIds,
          strategy: urlStrategies.string
        }
      ]
    : [];

  // Search listing page parameter - only include if there's actually a search listing component
  const searchListingPageConfig =
    isSearchListingPage() && document.querySelector('[data-module="searchListing"]')
      ? [
          {
            paramKey: 'searchListingPage',
            getValue: () => searchListingPageNumber,
            setValue: setSearchListingPageNumber,
            strategy: urlStrategies.number
          }
        ]
      : [];

  // Product-specific configurations (only on product listing pages)
  // Avoid writing initial slider values to URL until user changes them by comparing to baselines
  const productContainerEl = document.querySelector(
    '[data-module="productSectionFacet"]'
  ) as HTMLElement | null;

  // Parsing range from URL no longer needed for baseline comparison

  // We only use attribute baselines to decide persistence
  // const baselineRangeFromUrl = parseRangeFromUrl();
  const baselineRangeFromAttrs = (() => {
    try {
      const minAttr = productContainerEl?.dataset.maxLoadCapacityMin;
      const maxAttr = productContainerEl?.dataset.maxLoadCapacityMax;
      if (minAttr != null && maxAttr != null) {
        const a = Number(minAttr);
        const b = Number(maxAttr);
        if (isFinite(a) && isFinite(b)) return [a, b] as [number, number];
      }
    } catch {
      /* noop */
    }
    return null;
  })();
  // baselineRange is no longer used; rely on attribute baselines to decide whether to persist

  // const baselineSingleFromUrl = (() => {
  //   const raw = urlStateManager.getParam('productSingleSlider');
  //   if (!raw) return null;
  //   const n = Number(raw);
  //   return isFinite(n) ? n : null;
  // })();
  const baselineSingleFromAttrs = (() => {
    try {
      const minAttr = productContainerEl?.dataset.maxLiftHeightMin;
      const maxAttr = productContainerEl?.dataset.maxLiftHeightMax;
      const min = minAttr != null ? Number(minAttr) : undefined;
      const max = maxAttr != null ? Number(maxAttr) : undefined;
      if (isFinite(Number(min)) && isFinite(Number(max))) return Number(max);
      if (isFinite(Number(max))) return Number(max);
    } catch {
      /* noop */
    }
    return null;
  })();
  // baselineSingle is no longer used; rely on attribute baseline to decide whether to persist

  const arraysEqual = (a?: [number, number], b?: [number, number]) =>
    !!a && !!b && a[0] === b[0] && a[1] === b[1];

  const productConfigs = isProductListingPage()
    ? [
        {
          paramKey: 'productRangeSlider',
          getValue: () => productRangeSlider,
          setValue: (value: [number, number]) => {
            const validated = validateRange(value);
            if (!validated) return;
            const bounds = baselineRangeFromAttrs;
            const clamped = bounds
              ? clampRangeToBounds(validated, bounds[0], bounds[1])
              : validated;
            setProductRangeSlider(clamped);
          },
          strategy: {
            ...urlStrategies.json,
            shouldUpdate: (value: [number, number]) => {
              // Do not write when equal to attribute baselines
              if (baselineRangeFromAttrs && arraysEqual(value, baselineRangeFromAttrs))
                return false;
              return Array.isArray(value) && value.length === 2;
            }
          }
        },
        {
          paramKey: 'productSingleSlider',
          getValue: () => productSingleSlider.value,
          setValue: (value: number) => {
            const validated = validateSingle(value);
            if (validated === null) return;
            try {
              const minAttr = productContainerEl?.dataset.maxLiftHeightMin;
              const maxAttr = productContainerEl?.dataset.maxLiftHeightMax;
              const minBound = minAttr != null ? Number(minAttr) : undefined;
              const maxBound = maxAttr != null ? Number(maxAttr) : undefined;
              const next = clampNumberToBounds(validated, minBound, maxBound);
              setProductSingleSlider(next);
            } catch {
              setProductSingleSlider(validated);
            }
          },
          strategy: {
            ...urlStrategies.number,
            shouldUpdate: (value: number) => {
              // Do not write when equal to attribute baseline (max)
              if (baselineSingleFromAttrs !== null && value === baselineSingleFromAttrs)
                return false;
              return !isNaN(value as unknown as number);
            }
          }
        },
        {
          paramKey: 'primary-function-ids',
          getValue: () => csvPrimaryFunction,
          setValue: (csv: string) => setProductSectionIds('primary-function', csv),
          strategy: urlStrategies.string
        },
        {
          paramKey: 'application-type-ids',
          getValue: () => csvApplicationType,
          setValue: (csv: string) => setProductSectionIds('application-type', csv),
          strategy: urlStrategies.string
        },
        {
          paramKey: 'power-type-ids',
          getValue: () => csvPowerType,
          setValue: (csv: string) => setProductSectionIds('power-type', csv),
          strategy: urlStrategies.string
        },
        {
          paramKey: 'industry-ids',
          getValue: () => csvIndustry,
          setValue: (csv: string) => setProductSectionIds('industry', csv),
          strategy: urlStrategies.string
        }
      ]
    : [];

  // Combine all configurations
  const allConfigs = [
    ...baseConfigs,
    ...listingConfigs,
    ...pageConfig,
    ...searchListingConfigs,
    ...searchListingPageConfig,
    ...productConfigs
  ];

  // Create and use the URL sync hook directly
  try {
    const syncHook = createUrlSync(urlStateManager, ...allConfigs);
    syncHook();
  } catch {
    /* noop */
  }
};
